import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TablePagination,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    IconButton,
    Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Add User Modal states
    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        role: "user",
        is_active: true,
        gender: "",
        password: "",
    });
    const [addError, setAddError] = useState("");

    // Delete Confirmation Dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


    // Fetch users from backend
    const fetchUsers = async () => {
        setLoading(true);
        setError("");
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API_BASE_URL}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(res.data);
        } catch (err) {
            setError("Failed to load users.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Handle pagination change
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Open Add User modal
    const handleAddOpen = () => setAddOpen(true);
    const handleAddClose = () => {
        setAddOpen(false);
        setAddForm({
            full_name: "",
            email: "",
            phone: "",
            role: "user",
            is_active: true,
            gender: "",
        });
        setAddError("");
    };

    // Handle Add form input change
    const handleAddChange = (e) => {
        setAddForm({ ...addForm, [e.target.name]: e.target.value });
        setAddError("");
    };

    // Submit Add User form
    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setAddError("");
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/admin/users`, addForm, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchUsers();
            handleAddClose();
        } catch (err) {
            setAddError(
                err.response?.data?.detail || "Failed to add user."
            );
        }
    };

    // Open Delete confirmation dialog
    const handleDeleteClick = (userId) => {
        setDeleteUserId(userId);
        setDeleteOpen(true);
    };

    // Close Delete dialog
    const handleDeleteClose = () => {
        setDeleteOpen(false);
        setDeleteUserId(null);
    };

    // Confirm delete user
    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${API_BASE_URL}/admin/users/${deleteUserId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchUsers();
        } catch (err) {
            setError("Failed to delete user.");
        } finally {
            handleDeleteClose();
        }
    };

    // Toggle user active status
    const handleToggleActive = async (userId, currentStatus) => {
        try {
            const token = localStorage.getItem("token");
            await axios.patch(
                `${API_BASE_URL}/admin/users/${userId}/status`,
                null, // no body
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { is_active: !currentStatus }, // Send as query param
                }
            );
            fetchUsers(); // Refresh user list
        } catch (err) {
            setError("Failed to update user status.");
        }
    };

    return (
        <Box p={3}>
            <Typography variant="h4" mb={3}>
                User Management
            </Typography>

            {error && (
                <Typography color="error" mb={2}>
                    {error}
                </Typography>
            )}

            <Button variant="contained" onClick={handleAddOpen} sx={{ mb: 2 }}>
                Add User
            </Button>

            <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Full Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading &&
                            users
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((user) => (
                                    <TableRow key={user.id || user._id}>
                                        <TableCell>{user.full_name}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.phone}</TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={user.is_active}
                                                onChange={() =>
                                                    handleToggleActive(user.id || user._id, user.is_active)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Delete User">
                                                <IconButton onClick={() => handleDeleteClick(user.id || user._id)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={users.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>

            {/* Add User Modal */}
            <Dialog open={addOpen} onClose={handleAddClose} fullWidth maxWidth="sm">
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        onSubmit={handleAddSubmit}
                        noValidate
                        sx={{ mt: 2 }}
                    >
                        <TextField
                            label="Full Name"
                            name="full_name"
                            value={addForm.full_name}
                            onChange={handleAddChange}
                            required
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label="Email"
                            name="email"
                            value={addForm.email}
                            onChange={handleAddChange}
                            required
                            type="email"
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            label="Phone"
                            name="phone"
                            value={addForm.phone}
                            onChange={handleAddChange}
                            fullWidth
                            margin="normal"
                        />
                        <TextField
                            select
                            label="Role"
                            name="role"
                            value={addForm.role}
                            onChange={handleAddChange}
                            fullWidth
                            margin="normal"
                            SelectProps={{ native: true }}
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </TextField>
                        <TextField
                            select
                            label="Gender"
                            name="gender"
                            value={addForm.gender}
                            onChange={handleAddChange}
                            fullWidth
                            margin="normal"
                            SelectProps={{ native: true }}
                        >
                            <option value="">None</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </TextField>
                        <TextField
                            label="Password"
                            name="password"
                            value={addForm.password}
                            onChange={handleAddChange}
                            fullWidth
                            margin="normal"
                        />

                        <DialogActions>
                            <Button onClick={handleAddClose}>Cancel</Button>
                            <Button type="submit" variant="contained">
                                Add User
                            </Button>
                        </DialogActions>
                    </Box>
                    {addError && (
                        <Typography color="error" mt={2}>
                            {addError}
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteOpen} onClose={handleDeleteClose}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this user? This action cannot be
                    undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteClose}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteConfirm}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminUserManagement;
